/**
 * Code registry for type-bound Workspace/Peek viewers (Highlight-slot tabs).
 *
 * Media types show in Peek + Workspace. Business documents (PO, Invoice, …)
 * show in Workspace only (`showInPeek: false`).
 */

const TYPE_VIEWER_REGISTRY = Object.freeze({
  DOCUMENT: Object.freeze({ showInPeek: true, kind: 'media', screenKey: 'DOCUMENT' }),
  FILE: Object.freeze({ showInPeek: true, kind: 'media', screenKey: 'FILE' }),
  PHOTO: Object.freeze({ showInPeek: true, kind: 'media', screenKey: 'PHOTO' }),
  VIDEO: Object.freeze({ showInPeek: true, kind: 'media', screenKey: 'VIDEO' }),
  PURCHASE_ORDER: Object.freeze({
    showInPeek: false,
    kind: 'document',
    screenKey: 'PURCHASE_ORDER',
  }),
  PROPOSAL: Object.freeze({
    showInPeek: false,
    kind: 'document',
    screenKey: 'PROPOSAL',
  }),
  INVOICE: Object.freeze({
    showInPeek: false,
    kind: 'document',
    screenKey: 'INVOICE',
  }),
});

/** Alternate type keys that should resolve to a canonical registry entry. */
const TYPE_VIEWER_ALIASES = Object.freeze({
  PURCHASE_ORDERS: 'PURCHASE_ORDER',
  PO: 'PURCHASE_ORDER',
  PROPOSALS: 'PROPOSAL',
  INVOICES: 'INVOICE',
});

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeTypeViewerKey(value) {
  return String(value || '').trim().toUpperCase();
}

/**
 * @param {unknown} label
 * @returns {string}
 */
function normalizeViewerLabel(label) {
  return String(label || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * Resolve a canonical registry key from a type key and/or type definition.
 * Supports aliases (PURCHASE_ORDERS) and label fallback ("Purchase Order").
 *
 * @param {unknown} typeKey
 * @param {object|null|undefined} [nodeType]
 * @returns {string}
 */
export function resolveCanonicalTypeViewerKey(typeKey, nodeType = null) {
  const candidates = [
    normalizeTypeViewerKey(typeKey),
    normalizeTypeViewerKey(nodeType?.key),
  ].filter(Boolean);

  for (const candidate of candidates) {
    const aliased = TYPE_VIEWER_ALIASES[candidate] || candidate;
    if (TYPE_VIEWER_REGISTRY[aliased]) return aliased;

    // Soft match: purchase_order / purchaseorders / etc.
    if (/^PURCHASE_?ORDERS?$/.test(candidate)) {
      return 'PURCHASE_ORDER';
    }
    if (/^PROPOSALS?$/.test(candidate)) {
      return 'PROPOSAL';
    }
    if (/^INVOICES?$/.test(candidate)) {
      return 'INVOICE';
    }
  }

  const label = normalizeViewerLabel(nodeType?.label);
  const labelPlural = normalizeViewerLabel(nodeType?.labelPlural);
  if (
    label === 'purchase order'
    || label === 'purchase orders'
    || labelPlural === 'purchase orders'
    || labelPlural === 'purchase order'
  ) {
    return 'PURCHASE_ORDER';
  }

  if (
    label === 'proposal'
    || label === 'proposals'
    || labelPlural === 'proposals'
    || labelPlural === 'proposal'
  ) {
    return 'PROPOSAL';
  }

  if (
    label === 'invoice'
    || label === 'invoices'
    || labelPlural === 'invoices'
    || labelPlural === 'invoice'
  ) {
    return 'INVOICE';
  }

  return '';
}

/**
 * @param {unknown} typeKey
 * @param {object|null|undefined} [nodeType]
 * @returns {{ showInPeek: boolean, kind: string, screenKey: string }|null}
 */
export function getTypeViewerEntry(typeKey, nodeType = null) {
  const canonical = resolveCanonicalTypeViewerKey(typeKey, nodeType);
  if (!canonical) return null;
  return TYPE_VIEWER_REGISTRY[canonical] || null;
}

/**
 * Whether the Highlight-slot viewer tab should appear for this type.
 *
 * @param {unknown} typeKey
 * @param {{ surface?: 'peek'|'workspace', nodeType?: object|null }} [options]
 * @returns {boolean}
 */
export function shouldShowTypeViewerTab(typeKey, { surface = 'peek', nodeType = null } = {}) {
  const entry = getTypeViewerEntry(typeKey, nodeType);
  if (!entry) return false;
  if (surface === 'workspace') return true;
  return entry.showInPeek === true;
}

/**
 * @param {unknown} typeKey
 * @param {object|null|undefined} [nodeType]
 * @returns {boolean}
 */
export function isRegisteredTypeViewer(typeKey, nodeType = null) {
  return Boolean(getTypeViewerEntry(typeKey, nodeType));
}
